/**
 * engineStore
 *
 * Holds the rule pack (rules + jurisdictions) and the element facts the
 * engine evaluates against. Violations are always computed in the
 * BROWSER by running @vera/rules.evaluateRule over rule-pack.json ×
 * element-facts.json. Toggling the jurisdiction picker re-evaluates
 * instantly — no server round-trip, no re-running the runner script.
 *
 * Phase 3b will swap the JSON loaders for SPARQL queries against a real
 * triple store (Oxigraph locally, Context Graphs in production). The
 * shape of `loadedRules` and `loadedElements` is intentionally identical
 * to what those queries will return, so the swap is one file's worth of
 * change.
 *
 * History: this store previously carried a `mode: "static" | "live"`
 * field so the demo could toggle between the pre-computed
 * sample-violations.json file and the live in-browser engine. The
 * static path was deleted on 2026-05-18 once every visible violation
 * came from the engine; the toggle added a concept (two modes) for no
 * functional gain. The runner still writes sample-violations.json as
 * a side artifact for offline inspection but no UI loads it.
 */
import {
  type EvaluatedElement,
  evaluateRule,
  type RuleResult,
  type RuleSpec,
} from "@vera/rules";
import type { Violation } from "@vera/shared";
import { create } from "zustand";
import {
  latestUserFactsForElement,
  type UserFactRevision,
} from "../lib/userFactAuditDb";
import { auditRevisionsForRule, overridesForRule } from "../lib/userFactScope";
import type { UserFactValue } from "./userFactStore";
import {
  mergeElementWithUserFacts,
  userSuppliedProvenanceFacts,
} from "./userFactStore";

export interface JurisdictionInfo {
  id: string;
  displayName: string;
  rulesCount: number;
}

interface RulePackFile {
  modelRef: string;
  jurisdictions: JurisdictionInfo[];
  rules: RuleSpec[];
}

interface ElementFactsFile {
  modelRef: string;
  elements: EvaluatedElement[];
}

function jurisdictionKey(ids: string[]): string {
  return [...ids].sort().join("|");
}

function sameJurisdictionIds(a: string[], b: string[]): boolean {
  return jurisdictionKey(a) === jurisdictionKey(b);
}

function applyRulePackState(
  set: (
    partial: Partial<EngineStore> | ((s: EngineStore) => Partial<EngineStore>)
  ) => void,
  rp: RulePackFile,
  elements: EvaluatedElement[],
  inferred?: string[]
): void {
  const inferredIds = inferred ?? [];
  const packIds = new Set(rp.jurisdictions.map(j => j.id));

  set(s => {
    const inferredAndPresent = inferredIds.filter(id => packIds.has(id));
    const enabled: Record<string, boolean> = {};
    if (inferredAndPresent.length > 0) {
      for (const j of rp.jurisdictions) {
        enabled[j.id] = inferredAndPresent.includes(j.id);
      }
    } else {
      for (const j of rp.jurisdictions) enabled[j.id] = true;
    }
    const nextInferred =
      inferredAndPresent.length > 0 ? inferredAndPresent : inferredIds;
    const keepInferred = sameJurisdictionIds(
      s.inferredJurisdictionIds,
      nextInferred
    )
      ? s.inferredJurisdictionIds
      : nextInferred;

    return {
      loaded: true,
      loadError: null,
      jurisdictions: rp.jurisdictions,
      rules: rp.rules,
      elements,
      enabledJurisdictions: enabled,
      inferredJurisdictionIds: keepInferred,
      liveRunId: `live-${new Date().toISOString().replace(/[:.]/g, "-")}`,
    };
  });
}

interface EngineStore {
  /** Per-jurisdiction selection. Keys are jurisdictionId, values are enabled flag. */
  enabledJurisdictions: Record<string, boolean>;
  loaded: boolean;
  loadError: string | null;

  jurisdictions: JurisdictionInfo[];
  rules: RuleSpec[];
  elements: EvaluatedElement[];

  /**
   * Jurisdictions Vera deduced as actually applicable to the current
   * project. Used by the picker to style non-inferred jurisdictions
   * as "what-if overlays" and by load logic to set the initial
   * `enabledJurisdictions` map. Empty array means "no deduction
   * available — enable everything by default", preserving the
   * pre-deduction behaviour for callers that don't pass project
   * context.
   */
  inferredJurisdictionIds: string[];

  /** Last computed run id, so the inspector can show which evaluation it came from. */
  liveRunId: string;

  toggleJurisdiction: (id: string, enabled?: boolean) => void;
  /** Update deduction overlay when ACC project context changes (live browse). */
  setInferredJurisdictions: (ids: string[]) => void;
  loadFromUrls: (
    rulePackUrl: string,
    elementFactsUrl: string,
    options?: { inferredJurisdictionIds?: string[] }
  ) => Promise<void>;
  /** Load rule pack only — elements supplied later via setLiveElements (ACC live path). */
  loadRulesFromUrl: (
    rulePackUrl: string,
    options?: { inferredJurisdictionIds?: string[] }
  ) => Promise<void>;
  setLiveElements: (elements: EvaluatedElement[]) => void;
}

export const useEngineStore = create<EngineStore>(set => ({
  enabledJurisdictions: {},
  loaded: false,
  loadError: null,

  jurisdictions: [],
  rules: [],
  elements: [],
  inferredJurisdictionIds: [],
  liveRunId: "",

  toggleJurisdiction: (id, enabled) =>
    set(s => {
      const turningOn =
        enabled !== undefined ? enabled : !s.enabledJurisdictions[id];
      if (turningOn) {
        // Demo mode: one jurisdiction at a time — BCA vs LA vs NYC never
        // mix in the violations panel.
        const next: Record<string, boolean> = {};
        for (const j of s.jurisdictions) {
          next[j.id] = j.id === id;
        }
        return { enabledJurisdictions: next };
      }
      const next = { ...s.enabledJurisdictions, [id]: false };
      return { enabledJurisdictions: next };
    }),

  setInferredJurisdictions: ids => {
    set(s => {
      if (sameJurisdictionIds(s.inferredJurisdictionIds, ids)) {
        return {};
      }
      const packIds = new Set(s.jurisdictions.map(j => j.id));
      // Pack not loaded yet — keep the intended ids so ACC live path can start.
      if (packIds.size === 0) {
        return { inferredJurisdictionIds: ids };
      }
      const inferredAndPresent = ids.filter(id => packIds.has(id));
      const enabled: Record<string, boolean> = {};
      for (const j of s.jurisdictions) {
        enabled[j.id] = inferredAndPresent.includes(j.id);
      }
      return {
        inferredJurisdictionIds:
          inferredAndPresent.length > 0 ? inferredAndPresent : ids,
        enabledJurisdictions: enabled,
      };
    });
  },

  loadFromUrls: async (rulePackUrl, elementFactsUrl, options) => {
    set({
      loaded: false,
      loadError: null,
      jurisdictions: [],
      rules: [],
      elements: [],
      inferredJurisdictionIds: [],
      enabledJurisdictions: {},
    });
    try {
      const [rpRes, efRes] = await Promise.all([
        fetch(rulePackUrl),
        fetch(elementFactsUrl),
      ]);
      if (!rpRes.ok)
        throw new Error(`HTTP ${rpRes.status} loading ${rulePackUrl}`);
      if (!efRes.ok)
        throw new Error(`HTTP ${efRes.status} loading ${elementFactsUrl}`);

      const rp = (await rpRes.json()) as RulePackFile;
      const ef = (await efRes.json()) as ElementFactsFile;
      applyRulePackState(
        set,
        rp,
        ef.elements,
        options?.inferredJurisdictionIds
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[Vera] engineStore failed to load:", msg);
      set({ loaded: true, loadError: msg });
    }
  },

  loadRulesFromUrl: async (rulePackUrl, options) => {
    const pendingInferred =
      options?.inferredJurisdictionIds ??
      useEngineStore.getState().inferredJurisdictionIds;
    const st = useEngineStore.getState();
    if (
      st.loaded &&
      st.rules.length > 0 &&
      !st.loadError &&
      sameJurisdictionIds(st.inferredJurisdictionIds, pendingInferred)
    ) {
      return;
    }

    set({
      loaded: false,
      loadError: null,
      jurisdictions: [],
      rules: [],
      elements: [],
      inferredJurisdictionIds: pendingInferred,
      enabledJurisdictions: {},
    });
    try {
      const rpRes = await fetch(rulePackUrl);
      if (!rpRes.ok)
        throw new Error(`HTTP ${rpRes.status} loading ${rulePackUrl}`);
      const rp = (await rpRes.json()) as RulePackFile;
      applyRulePackState(set, rp, [], pendingInferred);
      console.info(
        "[Vera:ACC] Rule pack loaded for jurisdictions:",
        pendingInferred
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[Vera] engineStore failed to load rules:", msg);
      set({ loaded: true, loadError: msg });
    }
  },

  setLiveElements: elements =>
    set({
      elements,
      liveRunId: `live-acc-${new Date().toISOString().replace(/[:.]/g, "-")}`,
    }),
}));

/**
 * Compute violations for the currently-enabled jurisdictions, on demand,
 * in the browser. Pure function over the current store snapshot — Zustand
 * subscribers re-render automatically when any input changes.
 *
 * `defaultSheetByOntologyType` is passed in by the caller (App.tsx /
 * FormaApp.tsx read it off the active Project, since different
 * projects ship different sheet sets — Snowdon's rac-basic vs the
 * Casita Blanca ADU bundle).
 *
 * Honest caveat: this is not yet SPARQL-against-Oxigraph. It IS the same
 * `evaluateRule` function we ship in @vera/rules, running on the same
 * data shape Phase 3b will produce — so we're testing the engine, not
 * the storage layer.
 */
export function computeLiveViolations(
  state: EngineStore,
  defaultSheetByOntologyType: Record<string, string> = {},
  userFactOverrides: Record<number, Record<string, UserFactValue>> = {},
  projectId: string | null = null
): Violation[] {
  if (!state.loaded) return [];

  const violations: Violation[] = [];
  for (const rule of state.rules) {
    if (!state.enabledJurisdictions[rule.source.jurisdictionId]) continue;
    if (rule.confidence?.tier === "rejected") continue;
    const preferredSheet =
      defaultSheetByOntologyType[rule.appliesTo.elementClass];

    for (const el of state.elements) {
      const allAudit = projectId
        ? latestUserFactsForElement(projectId, el.dbId)
        : [];
      const auditRevisions = auditRevisionsForRule(allAudit, rule.ruleId);
      const scopedOverrides = overridesForRule(
        el.dbId,
        rule.ruleId,
        userFactOverrides,
        allAudit
      );
      const merged = mergeElementWithUserFacts(
        el,
        { [el.dbId]: scopedOverrides },
        auditRevisions
      );
      const r = evaluateRule(rule, merged);
      if (!r) continue;
      if (r.status === "compliant") continue;
      violations.push(
        ruleResultToViolation(
          r,
          rule,
          preferredSheet,
          state.liveRunId,
          auditRevisions.length > 0 ? auditRevisions : undefined
        )
      );
    }
  }
  return violations;
}

function ruleResultToViolation(
  rr: RuleResult,
  rule: RuleSpec,
  preferredSheet: string | undefined,
  runId: string,
  auditRevisions?: UserFactRevision[]
): Violation {
  const severity =
    rr.status === "confirmed_violation"
      ? rule.severity
      : rr.status === "indeterminate"
        ? "warning"
        : "info";
  return {
    id: `${rule.ruleId}#${rr.dbId}`.replace(/[^A-Za-z0-9_#:.-]/g, "_"),
    code: `${rule.source.code} §${rule.source.section}`,
    summary: rr.summary,
    rationale: rr.rationale,
    elementDbId: rr.dbId,
    preferredSheetId: preferredSheet,
    severity,
    sourceQueryHandle: `${rule.source.jurisdictionId}:${rule.source.section}`,
    provenance: {
      source: "engine",
      ruleId: rule.ruleId,
      runId,
      extraction: rule.extraction,
      confidence: rule.confidence,
      observed: rr.observedValue,
      threshold: rr.thresholdApplied,
      status: rr.status,
      exceptionsConsidered: rr.exceptionsConsidered,
      cite: rr.cite,
      ...(auditRevisions?.length
        ? {
            userSupplied: {
              facts: userSuppliedProvenanceFacts(auditRevisions),
            },
          }
        : {}),
    },
  };
}
